import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, User, Mail, ArrowRight, HelpCircle, X, Copy, Check } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API_ROOT } from "@/config";
import FeatureRequestButton from "@/components/ui/feature-request-button";
import SEO from "@/components/seo";
import { createCreatorLoaderTour, tourStyles } from "@/components/ui/tour-utils";
import LoadingSpinner from "@/components/ui/loading-spinner";

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

interface CampaignData {
  campaign_uuid: string;
  campaign_name: string;
  brand_name: string;
  product_name: string;
  requirements: any;
  brand_logo_path?: string;
  product_image_path?: string;
}

interface ExistingSubmission {
  submission_id?: string;
  analysis_id: string;
  status: string;
  score: number | null;
  passing?: boolean;
  created_at: string;
  version: number;
}

interface PendingAnalysis {
  analysis_id: string;
  status: string;
  score: number | null;
  passing?: boolean;
  created_at: string;
}

interface VersionCheckResponse {
  exists: boolean;
  version_count: number;
  next_version: number;
  submission?: ExistingSubmission;
  all_versions: ExistingSubmission[];
  pending_analysis?: PendingAnalysis | null;
  can_analyze?: boolean;
  usage?: number;
  limit?: number;
}

/**
 * Campaign Lobby Component
 * 
 * This component shows a "Lobby" screen where creators identify themselves
 * before uploading a video. This allows multiple creators to use the same
 * campaign link while keeping their submissions separate.
 * 
 * Flow: 
 * 1. /upload/:campaign_uuid -> Lobby Screen (enter name/email)
 * 2. Click "Start Submission" -> Check for existing session or create new
 * 3. Redirect to /video-upload with session data
 */
const CampaignLoader: React.FC = () => {
  const { campaign_uuid } = useParams<{ campaign_uuid: string }>();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campaignData, setCampaignData] = useState<CampaignData | null>(null);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showBriefingModal, setShowBriefingModal] = useState(false);
  const [copiedBriefing, setCopiedBriefing] = useState(false);
  
  // Lobby form state
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [creatorEmail, setCreatorEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingSubmission, setExistingSubmission] = useState<ExistingSubmission | null>(null);
  const [pendingAnalysis, setPendingAnalysis] = useState<PendingAnalysis | null>(null);
  const [showResumeOption, setShowResumeOption] = useState(false);
  const [versionCount, setVersionCount] = useState(0);
  const [nextVersion, setNextVersion] = useState(1);
  const [allVersions, setAllVersions] = useState<ExistingSubmission[]>([]);
  const [canAnalyze, setCanAnalyze] = useState(true);

  // Tour function
  const startTour = () => {
    const tour = createCreatorLoaderTour();
    tour.start();
  };

  const buildBriefingText = (): string => {
    if (!campaignData) return '';
    const lines: string[] = [];
    lines.push(`Campaign Briefing: ${campaignData.campaign_name}`);
    if (campaignData.brand_name) lines.push(`Brand: ${campaignData.brand_name}`);
    if (campaignData.product_name) lines.push(`Product: ${campaignData.product_name}`);
    lines.push('');

    const req = campaignData.requirements;
    if (!req) return lines.join('\n');

    if (Array.isArray(req)) {
      lines.push('Requirements:');
      req.filter((r: any) => r && typeof r === 'string' && r.trim()).forEach((r: string) => lines.push(`  • ${r}`));
    } else if (typeof req === 'object') {
      const names: Record<string, string> = {
        hooks: 'Hooks', topics: 'Topics', call_to_actions: 'Call to Actions',
        elements: "Don'ts", story: 'Story', visuals_background: 'Visuals & Background',
      };
      const order = ['hooks', 'topics', 'call_to_actions', 'elements', 'story', 'visuals_background'];
      for (const key of order) {
        const val = req[key];
        if (Array.isArray(val)) {
          const items = val.filter((i: any) => i && typeof i === 'string' && i.trim());
          if (items.length) {
            lines.push(`${names[key] || key}:`);
            items.forEach((i: string) => lines.push(`  • ${i}`));
            lines.push('');
          }
        } else if (typeof val === 'string' && val.trim()) {
          lines.push(`${names[key] || key}:`);
          lines.push(`  ${val}`);
          lines.push('');
        }
      }
    }
    return lines.join('\n');
  };

  const handleCopyBriefing = async () => {
    try {
      await navigator.clipboard.writeText(buildBriefingText());
      setCopiedBriefing(true);
      setTimeout(() => setCopiedBriefing(false), 2000);
    } catch {
      console.error('Failed to copy briefing');
    }
  };

  // Load campaign data on mount
  useEffect(() => {
    const loadCampaign = async () => {
      if (!campaign_uuid) {
        setError('Invalid campaign link');
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_ROOT}/campaigns/${campaign_uuid}`);
        
        if (!response.ok) {
          throw new Error('Campaign not found or expired');
        }

        const data: CampaignData = await response.json();
        console.log('📥 Campaign data received:', data);
        
        if (!data.requirements) {
          throw new Error('Campaign requirements not found');
        }
        
        setCampaignData(data);
        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load campaign');
        setIsLoading(false);
      }
    };

    loadCampaign();
  }, [campaign_uuid]);

  // Show onboarding modal when campaign data loads
  useEffect(() => {
    if (!isLoading && campaignData) {
      // Small delay to ensure page is rendered before showing modal
      setTimeout(() => {
        setShowOnboardingModal(true);
      }, 500);
    }
  }, [isLoading, campaignData]);

  // Email validation function
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check for existing submission when creator enters their email
  const checkExistingSubmission = async () => {
    if (!creatorEmail.trim() || !validateEmail(creatorEmail.trim()) || !campaign_uuid) return;
    
    try {
      const response = await fetch(
        `${API_ROOT}/campaigns/${campaign_uuid}/check-submission?creator_email=${encodeURIComponent(creatorEmail.trim())}`
      );
      
      if (response.ok) {
        const data: VersionCheckResponse = await response.json();
        setVersionCount(data.version_count);
        setNextVersion(data.next_version);
        setAllVersions(data.all_versions || []);
        setPendingAnalysis(data.pending_analysis || null);
        setCanAnalyze(data.can_analyze !== false);
        
        if (data.exists && data.submission) {
          setExistingSubmission(data.submission);
          setShowResumeOption(true);
        } else {
          setExistingSubmission(null);
          setShowResumeOption(false);
        }
      }
    } catch (err) {
      console.log('No existing submission found');
      setExistingSubmission(null);
      setPendingAnalysis(null);
      setShowResumeOption(false);
      setVersionCount(0);
      setNextVersion(1);
      setAllVersions([]);
      setCanAnalyze(true);
    }
  };

  // Auto-check for existing submissions when all fields are filled and valid
  useEffect(() => {
    if (
      firstName.trim() && 
      surname.trim() && 
      creatorEmail.trim() && 
      validateEmail(creatorEmail.trim()) &&
      campaign_uuid
    ) {
      checkExistingSubmission();
    } else {
      // Reset if fields are not complete
      setShowResumeOption(false);
      setExistingSubmission(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, surname, creatorEmail, campaign_uuid]);

  // Discard pending analysis and start fresh
  const handleDiscardAndStartFresh = async () => {
    if (!campaign_uuid || !creatorEmail.trim()) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Call backend to discard the pending analysis
      const response = await fetch(`${API_ROOT}/campaigns/${campaign_uuid}/discard-pending`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          creator_email: creatorEmail.trim(),
          analysis_id: pendingAnalysis?.analysis_id
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to discard pending analysis');
      }
      
      const result = await response.json();
      console.log(`🗑️ Discarded ${result.discarded_count} pending analysis(es)`);
      
      // Clear the pending analysis state
      setPendingAnalysis(null);
      
      // Now proceed with starting a new submission
      await handleStartSubmission(false);
    } catch (err) {
      console.error('Failed to discard pending:', err);
      setError('Failed to discard pending analysis. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Start new submission or resume existing
  const handleStartSubmission = async (resumeExisting: boolean = false) => {
    // Validation
    if (!firstName.trim()) {
      setError('Please enter your first name');
      return;
    }
    if (!surname.trim()) {
      setError('Please enter your surname');
      return;
    }
    if (!creatorEmail.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (!validateEmail(creatorEmail.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (!campaignData || !campaign_uuid) return;

    setIsSubmitting(true);
    setError(null);
    setEmailError(null);

    try {
      // If resuming and we have an existing analysis, go directly to results
      if (resumeExisting && existingSubmission?.analysis_id) {
        navigate(`/campaign-result/${existingSubmission.analysis_id}?campaign=${campaign_uuid}`);
        return;
      }
      
      // Check for existing submissions before proceeding (to get correct version)
      let actualNextVersion = nextVersion;
      try {
        const response = await fetch(
          `${API_ROOT}/campaigns/${campaign_uuid}/check-submission?creator_email=${encodeURIComponent(creatorEmail.trim())}`
        );
        if (response.ok) {
          const data: VersionCheckResponse = await response.json();
          actualNextVersion = data.next_version;
          console.log(`📊 Version check before submit: ${data.version_count} existing, next is V${actualNextVersion}`);
          
          if (data.can_analyze === false) {
            setCanAnalyze(false);
            setIsSubmitting(false);
            return;
          }
          
          // If there are existing submissions and user hasn't seen the warning yet, show it
          if (data.exists && !showResumeOption) {
            setVersionCount(data.version_count);
            setNextVersion(data.next_version);
            setAllVersions(data.all_versions || []);
            setExistingSubmission(data.submission || null);
            setShowResumeOption(true);
            setIsSubmitting(false);
            return; // Stop here to let user decide
          }
        }
      } catch (err) {
        console.log('Could not check existing submissions, proceeding with V1');
        actualNextVersion = 1;
      }

      // Store session data in localStorage
      localStorage.setItem('campaign_mode', 'true');
      localStorage.setItem('campaign_uuid', campaign_uuid);
      localStorage.setItem('campaign_name', campaignData.campaign_name);
      localStorage.setItem('campaign_brand', campaignData.brand_name || '');
      localStorage.setItem('campaign_product', campaignData.product_name || '');
      localStorage.setItem('campaign_requirements', JSON.stringify(campaignData.requirements));
      
      // Store creator identification
      localStorage.setItem('creator_first_name', firstName.trim());
      localStorage.setItem('creator_surname', surname.trim());
      localStorage.setItem('creator_email', creatorEmail.trim());
      // Keep creator_name for backward compatibility (full name)
      localStorage.setItem('creator_name', `${firstName.trim()} ${surname.trim()}`);
      
      // Store version info for new submission (use the freshly fetched version)
      localStorage.setItem('submission_version', actualNextVersion.toString());
      console.log(`📊 Starting submission V${actualNextVersion}`);

      // Navigate to video upload
      navigate('/video-upload?campaign=true');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start submission');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return <LoadingSpinner size="lg" text="Loading campaign..." fullPage />;
  }

  // Error state
  if (error && !campaignData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: PALETTE.brightSnow }}>
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: PALETTE.orange }} />
            <h2 className="text-xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>Campaign Not Found</h2>
            <p style={{ color: `${PALETTE.gunmetal}70` }}>{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Lobby Screen
  return (
    <div 
      className="min-h-screen flex flex-col relative"
      style={{ 
        background: `linear-gradient(135deg, ${PALETTE.brightSnow} 0%, ${PALETTE.americanSilver}30 100%)`
      }}
    >
      <SEO
        title="Upload Your Video"
        description="Upload your video to check compliance against the campaign brief. BrandComply helps creators submit on-brand content."
        path={`/upload/${campaign_uuid}`}
      />
      {/* Feature Request & Help/Tour Buttons - Top Right */}
      <div className="absolute top-4 right-8 z-10 flex items-center gap-3">
        <FeatureRequestButton />
        <button
          onClick={startTour}
          aria-label="Help / Tour"
          className="tour-help-btn bg-white border border-gray-300 shadow-md rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition"
          style={{ boxShadow: '0 2px 8px rgba(36,56,55,0.10)' }}
        >
          <HelpCircle size={20} color={PALETTE.gunmetal} />
        </button>
      </div>

      {/* Powered By - Bottom Right */}
      <div className="fixed bottom-6 right-6 z-50 flex items-center gap-4">
        <span className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
          powered by{' '}
          <a 
            href="https://brandcomply.ai" 
            target="_blank" 
            rel="noopener noreferrer"
            className="font-semibold hover:underline"
            style={{ color: PALETTE.gunmetal }}
          >
            BrandComply
          </a>
        </span>
      </div>

      {/* Tour styles */}
      <style>{tourStyles}</style>
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-6 py-12">
            {/* Process Wizard - Step 1: Contact Information */}
            <div className="flex items-center justify-center gap-4 mb-12 w-full max-w-4xl">
              {['Contact Information', 'Video Upload', 'Success Scan'].map((label, idx) => {
                const isActive = idx === 0; // Step 1 (Contact Information) is active
                const isCompleted = false;
                return (
                  <React.Fragment key={label}>
                    <div className="flex items-center flex-1 justify-center">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                        style={{
                          background: isActive ? PALETTE.gunmetal : (isCompleted ? PALETTE.inchworm : PALETTE.americanSilver),
                          color: isActive ? PALETTE.brightSnow : PALETTE.gunmetal
                        }}
                      >
                        {idx + 1}
                      </div>
                      <span 
                        className="ml-2 text-sm font-medium whitespace-nowrap hidden sm:inline"
                        style={{ color: isActive || isCompleted ? PALETTE.gunmetal : PALETTE.americanSilver }}
                      >
                        {label}
                      </span>
                    </div>
                    {idx < 2 && (
                      <div
                        className="h-0.5 mx-2 sm:mx-4 flex-1"
                        style={{
                          minWidth: 20,
                          background: isCompleted ? PALETTE.inchworm : PALETTE.americanSilver,
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <div className="flex justify-center">
              <Card className="w-full max-w-lg shadow-xl border-0">
        <CardContent className="p-8">
                   
          {/* Campaign Info */}
          {campaignData && (
            <div className="campaign-info-section mb-6 pb-6 border-b" style={{ borderColor: PALETTE.americanSilver }}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                  {campaignData.campaign_name}
                  {campaignData.brand_name && (
                    <span className="font-normal" style={{ color: `${PALETTE.gunmetal}70` }}>
                      {' '}by {campaignData.brand_name}
                    </span>
                  )}
                </p>
                <Button
                  onClick={() => setShowBriefingModal(true)}
                  variant="outline"
                  className="rounded-full px-4 h-8 text-xs font-semibold flex items-center gap-1.5"
                  style={{
                    background: PALETTE.brightSnow,
                    color: PALETTE.gunmetal,
                    border: `1px solid ${PALETTE.americanSilver}`,
                    borderRadius: '9999px'
                  }}
                >
                  View Briefing
                </Button>
              </div>
            </div>
          )}

          {/* Identification Form */}
          <div className="space-y-4">
            {/* First Name and Surname on same line */}
            <div className="name-fields grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
                  First Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setError(null);
                  }}
                  placeholder="First name"
                  className="h-12 text-base"
                  style={{ borderColor: PALETTE.americanSilver }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
                  Surname <span className="text-red-500">*</span>
                </label>
                <Input
                  value={surname}
                  onChange={(e) => {
                    setSurname(e.target.value);
                    setError(null);
                  }}
                  placeholder="Surname"
                  className="h-12 text-base"
                  style={{ borderColor: PALETTE.americanSilver }}
                />
              </div>
            </div>

            <div className="email-field">
              <label className="block text-sm font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: PALETTE.americanSilver }} />
                <Input
                  type="email"
                  value={creatorEmail}
                  onChange={(e) => {
                    const emailValue = e.target.value;
                    setCreatorEmail(emailValue);
                    setError(null);
                    
                    // Real-time email validation
                    if (emailValue.trim() && !validateEmail(emailValue.trim())) {
                      setEmailError('Please enter a valid email address');
                    } else {
                      setEmailError(null);
                    }
                  }}
                  onBlur={() => {
                    // Validate on blur
                    if (creatorEmail.trim() && !validateEmail(creatorEmail.trim())) {
                      setEmailError('Please enter a valid email address');
                    } else {
                      setEmailError(null);
                    }
                  }}
                  placeholder="you@example.com"
                  className="pl-10 h-12 text-base"
                  style={{ 
                    borderColor: emailError ? PALETTE.orange : PALETTE.americanSilver 
                  }}
                />
              </div>
              {emailError && (
                <div className="mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: PALETTE.orange }} />
                  <p className="text-xs" style={{ color: PALETTE.orange }}>
                    {emailError}
                  </p>
                </div>
              )}
            </div>

            {/* Pending Analysis - Not yet submitted */}
            {pendingAnalysis && !showResumeOption && (
              <div 
                className="p-4 rounded-lg border"
                style={{ 
                  background: `${PALETTE.paleViolet}10`,
                  borderColor: PALETTE.paleViolet
                }}
              >
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium" style={{ color: PALETTE.gunmetal }}>
                      Pending Submission
                    </p>
                    <p className="text-sm font-medium" style={{ color: PALETTE.gunmetal }}>
                      BrandComply score: {pendingAnalysis.score !== null ? `${Math.round(pendingAnalysis.score)}%` : 'Analyzing...'}
                    </p>
                  </div>
                  {pendingAnalysis.created_at && (
                    <p className="text-xs" style={{ color: `${PALETTE.gunmetal}60` }}>
                      {(() => {
                        try {
                          // Parse UTC timestamp from backend
                          const timestamp = pendingAnalysis.created_at;
                          let date: Date;
                          if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+')) {
                            const utcTimestamp = timestamp.replace(' ', 'T') + 'Z';
                            date = new Date(utcTimestamp);
                          } else {
                            date = new Date(timestamp);
                          }
                          if (isNaN(date.getTime())) return '';
                          return date.toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          });
                        } catch (e) {
                          return '';
                        }
                      })()}
                    </p>
                  )}
                  <p className="text-xs mt-2" style={{ color: `${PALETTE.gunmetal}70` }}>
                    You have an unsubmitted analysis. You can continue with it or start fresh with a new video.
                  </p>
                </div>
                
                {/* Two options: Continue or Start Fresh */}
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      if (pendingAnalysis && campaign_uuid) {
                        navigate(`/campaign-result/${pendingAnalysis.analysis_id}?campaign=${campaign_uuid}`);
                      }
                    }}
                    className="flex-1 h-9 text-sm rounded-full"
                    style={{ 
                      background: PALETTE.paleViolet,
                      color: PALETTE.brightSnow,
                      border: 'none',
                      borderRadius: '9999px'
                    }}
                  >
                    Continue Pending
                  </Button>
                  <Button
                    onClick={handleDiscardAndStartFresh}
                    disabled={isSubmitting}
                    variant="outline"
                    className="flex-1 h-9 text-sm rounded-full"
                    style={{ 
                      borderColor: PALETTE.americanSilver,
                      color: PALETTE.gunmetal,
                      borderRadius: '9999px'
                    }}
                  >
                    {isSubmitting ? 'Starting...' : 'Start Fresh'}
                  </Button>
                </div>
              </div>
            )}

            {/* Previous Submissions / Version Info - Minimal Design */}
            {showResumeOption && existingSubmission && (
              <div>
                {/* Pending Analysis Section - if exists along with submitted versions */}
                {pendingAnalysis && (
                  <div 
                    className="p-4 rounded-lg border mb-3"
                    style={{ 
                      background: `${PALETTE.paleViolet}10`,
                      borderColor: PALETTE.paleViolet
                    }}
                  >
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium" style={{ color: PALETTE.gunmetal }}>
                          📊 Pending Analysis
                        </p>
                        <p className="text-sm font-bold" style={{ color: PALETTE.gunmetal }}>
                          {pendingAnalysis.score !== null ? `${Math.round(pendingAnalysis.score)}%` : 'Analyzing...'}
                        </p>
                      </div>
                      {pendingAnalysis.created_at && (
                        <p className="text-xs" style={{ color: `${PALETTE.gunmetal}60` }}>
                          {(() => {
                            try {
                              // Parse UTC timestamp from backend
                              const timestamp = pendingAnalysis.created_at;
                              let date: Date;
                              if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+')) {
                                const utcTimestamp = timestamp.replace(' ', 'T') + 'Z';
                                date = new Date(utcTimestamp);
                              } else {
                                date = new Date(timestamp);
                              }
                              if (isNaN(date.getTime())) return '';
                              return date.toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              });
                            } catch (e) {
                              return '';
                            }
                          })()}
                        </p>
                      )}
                      <p className="text-xs mt-2" style={{ color: `${PALETTE.gunmetal}70` }}>
                        You have an unsubmitted analysis. You can continue with it or start fresh with a new video.
                      </p>
                    </div>
                    
                    {/* Two options: Continue or Start Fresh */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          if (pendingAnalysis && campaign_uuid) {
                            navigate(`/campaign-result/${pendingAnalysis.analysis_id}?campaign=${campaign_uuid}`);
                          }
                        }}
                        className="flex-1 h-9 text-sm rounded-full"
                        style={{ 
                          background: PALETTE.paleViolet,
                          color: PALETTE.brightSnow,
                          border: 'none',
                          borderRadius: '9999px'
                        }}
                      >
                        Continue Pending
                      </Button>
                      <Button
                        onClick={handleDiscardAndStartFresh}
                        disabled={isSubmitting}
                        variant="outline"
                        className="flex-1 h-9 text-sm rounded-full"
                        style={{ 
                          borderColor: PALETTE.americanSilver,
                          color: PALETTE.gunmetal,
                          borderRadius: '9999px'
                        }}
                      >
                        {isSubmitting ? 'Starting...' : 'Start Fresh'}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Latest Submitted Version */}
                <div 
                  className="p-4 rounded-lg border"
                  style={{ 
                    background: PALETTE.brightSnow,
                    borderColor: PALETTE.americanSilver
                  }}
                >
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium" style={{ color: PALETTE.gunmetal }}>
                        Latest Submission: V{existingSubmission.version}
                      </p>
                      <p className="text-sm font-medium" style={{ color: PALETTE.gunmetal }}>
                        Compliance score: {existingSubmission.score !== null ? `${Math.round(existingSubmission.score)}%` : 'N/A'}
                      </p>
                    </div>
                    {existingSubmission.created_at && (
                      <p className="text-xs" style={{ color: `${PALETTE.gunmetal}60` }}>
                        {(() => {
                          try {
                            // Parse UTC timestamp from backend
                            const timestamp = existingSubmission.created_at;
                            let date: Date;
                            if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+')) {
                              const utcTimestamp = timestamp.replace(' ', 'T') + 'Z';
                              date = new Date(utcTimestamp);
                            } else {
                              date = new Date(timestamp);
                            }
                            if (isNaN(date.getTime())) return '';
                            return date.toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          } catch (e) {
                            return '';
                          }
                        })()}
                      </p>
                    )}
                  </div>
                  
                  {/* View Details Button */}
                  <Button
                    onClick={() => handleStartSubmission(true)}
                    variant="outline"
                    className="w-full h-9 text-sm rounded-full"
                    style={{ 
                      borderColor: PALETTE.americanSilver,
                      color: PALETTE.gunmetal
                    }}
                  >
                    View Details of V{existingSubmission.version}
                  </Button>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div 
                className="p-3 rounded-lg flex items-center gap-2"
                style={{ background: `${PALETTE.orange}10` }}
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: PALETTE.orange }} />
                <p className="text-sm" style={{ color: PALETTE.orange }}>{error}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
            </div>
          </div>
        </main>
      </div>

      {/* Fixed Bottom Button - Only show when form is valid AND no pending analysis */}
      {firstName.trim() && 
       surname.trim() && 
       creatorEmail.trim() && 
       validateEmail(creatorEmail.trim()) && 
       !pendingAnalysis?.analysis_id && (
        <div className="fixed bottom-8 left-0 right-0 z-20 flex justify-center">
          {!canAnalyze ? (
            <div className="mx-6 max-w-md p-4 rounded-2xl shadow-lg" style={{ background: '#FEF3C7', border: '1px solid #F59E0B' }}>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#D97706' }} />
                <div>
                  <p className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                    Submissions paused
                  </p>
                  <p className="text-xs mt-1" style={{ color: `${PALETTE.gunmetal}90` }}>
                    The brand's video check limit has been reached for this month. Please try again later or contact the brand directly.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <Button
              onClick={() => handleStartSubmission(false)}
              disabled={isSubmitting}
              className="start-submission-btn rounded-full px-8 h-12 text-base font-semibold transition-all duration-300 flex items-center gap-2"
              style={{ 
                background: isSubmitting ? PALETTE.americanSilver : PALETTE.inchworm,
                color: PALETTE.gunmetal,
                border: 'none',
                borderRadius: '9999px'
              }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-base font-semibold">Starting...</span>
                </>
              ) : (
                <>
                  <span className="text-base font-semibold">{nextVersion > 1 ? `Submit Version ${nextVersion}` : 'Start Submission'}</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Briefing Requirements Modal */}
      {showBriefingModal && campaignData && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowBriefingModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 p-8"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">                
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: PALETTE.gunmetal }}>
                    Campaign Briefing
                  </h2>
                  <p className="text-sm" style={{ color: `${PALETTE.gunmetal}70` }}>
                    {campaignData.campaign_name}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyBriefing}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 hover:opacity-80"
                  style={{
                    background: copiedBriefing ? `${PALETTE.inchworm}30` : `${PALETTE.americanSilver}20`,
                    color: copiedBriefing ? PALETTE.gunmetal : PALETTE.gunmetal,
                    border: `1px solid ${copiedBriefing ? PALETTE.inchworm : PALETTE.americanSilver}`,
                  }}
                >
                  {copiedBriefing ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copiedBriefing ? 'Copied!' : 'Copy Briefing'}
                </button>
                <button
                  onClick={() => setShowBriefingModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {/* Campaign Info */}
              <div className="p-4 rounded-lg" style={{ background: `${PALETTE.americanSilver}10` }}>
                <div className="grid grid-cols-2 gap-6">
                  {campaignData.brand_name && (
                    <div>
                      <p className="text-xs font-medium mb-1" style={{ color: `${PALETTE.gunmetal}60` }}>Brand</p>
                      <p className="text-sm font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>{campaignData.brand_name}</p>
                      
                      {campaignData.brand_logo_path && (
                        <div className="mt-3">
                          <p className="text-xs font-medium mb-2" style={{ color: `${PALETTE.gunmetal}60` }}>Brand Logo</p>
                          <div className="p-2 rounded border flex items-center justify-center bg-white" style={{ borderColor: `${PALETTE.americanSilver}60`, minHeight: '80px' }}>
                            <img 
                              src={campaignData.brand_logo_path} 
                              alt="Brand Logo" 
                              className="max-h-16 max-w-full object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                const parent = (e.target as HTMLElement).parentElement;
                                if (parent) {
                                  parent.innerHTML = '<p class="text-xs" style="color: #D1D1D1">Image unavailable</p>';
                                }
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {campaignData.product_name && (
                    <div>
                      <p className="text-xs font-medium mb-1" style={{ color: `${PALETTE.gunmetal}60` }}>Product</p>
                      <p className="text-sm font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>{campaignData.product_name}</p>
                      
                      {campaignData.product_image_path && (
                        <div className="mt-3">
                          <p className="text-xs font-medium mb-2" style={{ color: `${PALETTE.gunmetal}60` }}>Product Image</p>
                          <div className="p-2 rounded border flex items-center justify-center bg-white" style={{ borderColor: `${PALETTE.americanSilver}60`, minHeight: '80px' }}>
                            <img 
                              src={campaignData.product_image_path} 
                              alt="Product Image" 
                              className="max-h-16 max-w-full object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                const parent = (e.target as HTMLElement).parentElement;
                                if (parent) {
                                  parent.innerHTML = '<p class="text-xs" style="color: #D1D1D1">Image unavailable</p>';
                                }
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Requirements */}
              {(() => {
                try {
                  const requirementsData = campaignData.requirements;
                  
                  if (!requirementsData) {
                    return (
                      <div className="p-6 rounded-lg text-center" style={{ background: `${PALETTE.americanSilver}10` }}>
                        <p className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
                          No requirements available
                        </p>
                      </div>
                    );
                  }
                  
                  if (Array.isArray(requirementsData)) {
                    const validReqs = requirementsData.filter((r: any) => r && typeof r === 'string' && r.trim());
                    
                    if (validReqs.length === 0) {
                      return (
                        <div className="p-6 rounded-lg text-center" style={{ background: `${PALETTE.americanSilver}10` }}>
                          <p className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
                            No requirements available
                          </p>
                        </div>
                      );
                    }
                    
                    return (
                      <div>
                        <div className="p-3 rounded-lg mb-3" style={{ background: `${PALETTE.paleViolet}10`, border: `1px solid ${PALETTE.paleViolet}40` }}>
                          <p className="text-xs" style={{ color: `${PALETTE.gunmetal}80` }}>
                            ℹ️ This campaign was created with an older format. New campaigns will show requirements organized by category (Hooks, Topics, Call to Actions, Don'ts, Story, Visuals & Background).
                          </p>
                        </div>
                        <div className="p-4 rounded-lg" style={{ background: `${PALETTE.americanSilver}10` }}>
                          <h4 className="text-base font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>
                            All Requirements ({validReqs.length})
                          </h4>
                          <div className="space-y-2">
                            {validReqs.map((req: string, idx: number) => (
                              <div 
                                key={idx}
                                className="flex items-start gap-2 text-sm leading-relaxed"
                                style={{ color: PALETTE.gunmetal }}
                              >
                                <span className="flex-shrink-0 mt-1.5 w-1 h-1 rounded-full" style={{ background: PALETTE.gunmetal }}></span>
                                <span className="flex-1">{req}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  if (typeof requirementsData === 'object' && requirementsData !== null) {
                    const categoryDisplayNames: { [key: string]: string } = {
                      'hooks': 'Hooks',
                      'topics': 'Topics',
                      'call_to_actions': 'Call to Actions',
                      'elements': "Don'ts",
                      'story': 'Story',
                      'visuals_background': 'Visuals & Background'
                    };
                    
                    const categoryOrder = ['hooks', 'topics', 'call_to_actions', 'elements', 'story', 'visuals_background'];
                    
                    const categories = categoryOrder
                      .map(key => ({ key, value: requirementsData[key] }))
                      .filter(({ value }) => {
                        if (Array.isArray(value)) {
                          return value.some((item: any) => item && typeof item === 'string' && item.trim());
                        } else if (typeof value === 'string') {
                          return value.trim().length > 0;
                        }
                        return false;
                      });
                    
                    if (categories.length === 0) {
                      return (
                        <div className="p-6 rounded-lg text-center" style={{ background: `${PALETTE.americanSilver}10` }}>
                          <p className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
                            No requirements available
                          </p>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="space-y-4">
                        {categories.map(({ key, value }) => {
                          const displayName = categoryDisplayNames[key] || key;
                          const isTextCategory = key === 'story' || key === 'visuals_background';
                          
                          return (
                            <div key={key} className="p-4 rounded-lg" style={{ background: `${PALETTE.americanSilver}10` }}>
                              {Array.isArray(value) ? (
                                <>
                                  <h4 className="text-base font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>
                                    {displayName} ({value.filter((i: any) => i && i.trim()).length})
                                  </h4>
                                  <div className="space-y-2">
                                    {value.filter((item: any) => item && item.trim()).map((item: string, idx: number) => (
                                      <div 
                                        key={idx}
                                        className="flex items-start gap-2 text-sm leading-relaxed"
                                        style={{ color: PALETTE.gunmetal }}
                                      >
                                        <span className="flex-shrink-0 mt-1.5 w-1 h-1 rounded-full" style={{ background: PALETTE.gunmetal }}></span>
                                        <span className="flex-1">{item}</span>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              ) : typeof value === 'string' && isTextCategory ? (
                                <>
                                  <h4 className="text-base font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>
                                    {displayName}
                                  </h4>
                                  <p className="text-sm leading-relaxed" style={{ color: PALETTE.gunmetal }}>
                                    {value}
                                  </p>
                                </>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  
                  return (
                    <div className="p-6 rounded-lg text-center" style={{ background: `${PALETTE.americanSilver}10` }}>
                      <p className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
                        No requirements available
                      </p>
                    </div>
                  );
                } catch (error) {
                  console.error('Error displaying requirements:', error);
                  return (
                    <div className="p-6 rounded-lg text-center" style={{ background: `${PALETTE.americanSilver}10` }}>
                      <p className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
                        Error loading requirements
                      </p>
                    </div>
                  );
                }
              })()}
            </div>

            <div className="flex justify-end mt-6">
              <Button
                onClick={() => setShowBriefingModal(false)}
                className="rounded-full px-6 h-10"
                style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal, borderRadius: '9999px' }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Modal */}
      <Dialog open={showOnboardingModal} onOpenChange={setShowOnboardingModal}>
        <DialogContent 
          className="sm:max-w-xl"
          style={{
            backgroundColor: PALETTE.brightSnow,
            border: `1px solid ${PALETTE.americanSilver}`,
            borderRadius: '8px',
            padding: '32px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)'
          }}
        >
          <div className="space-y-5">
            <div className="text-center mb-2">
              <DialogTitle 
                className="text-xl font-semibold mb-2"
                style={{ color: PALETTE.gunmetal }}
              >
                Your Pre-Submission Self-Check
              </DialogTitle>
              <p 
                className="text-sm"
                style={{ color: `${PALETTE.gunmetal}99` }}
              >
                We help you catch missing requirements instantly so you can submit with confidence, and avoid revisions.
              </p>
            </div>
            
            <div className="space-y-4">
              {/* Requirement Scan */}
              <div className="flex gap-3">
                <span className="text-lg flex-shrink-0">✅</span>
                <div>
                  <p 
                    className="text-sm font-semibold mb-1"
                    style={{ color: PALETTE.gunmetal }}
                  >
                    Requirement Scan
                  </p>
                  <p 
                    className="text-sm leading-relaxed"
                    style={{ color: `${PALETTE.gunmetal}cc` }}
                  >
                    We verify the "must-haves" (e.g. hooks, brand mentions, CTAs) so you don't have to worry about something missing.
                  </p>
                </div>
              </div>
              
              {/* Creative Freedom */}
              <div className="flex gap-3">
                <span className="text-lg flex-shrink-0">🎨</span>
                <div>
                  <p 
                    className="text-sm font-semibold mb-1"
                    style={{ color: PALETTE.gunmetal }}
                  >
                    Creative Freedom
                  </p>
                  <p 
                    className="text-sm leading-relaxed"
                    style={{ color: `${PALETTE.gunmetal}cc` }}
                  >
                    Our system is objective. Your creativity, storytelling, and style are left entirely to the human review of the agency/brand.
                  </p>
                </div>
              </div>
              
              {/* Private Feedback */}
              <div className="flex gap-3">
                <span className="text-lg flex-shrink-0">🛡️</span>
                <div>
                  <p 
                    className="text-sm font-semibold mb-1"
                    style={{ color: PALETTE.gunmetal }}
                  >
                    Private Feedback
                  </p>
                  <p 
                    className="text-sm leading-relaxed"
                    style={{ color: `${PALETTE.gunmetal}cc` }}
                  >
                    Preliminary scans and drafts are 100% confidential. The agency/brand only sees the final version you choose to submit.
                  </p>
                </div>
              </div>
            </div>
            
            <p 
              className="text-sm leading-relaxed pt-2"
              style={{ color: `${PALETTE.gunmetal}cc` }}
            >
              <span className="font-semibold" style={{ color: PALETTE.gunmetal }}>Why use this?</span> It ensures you hit the brief perfectly the first time, eliminating the revision loops and speeding up your approval.
            </p>
            
            <Button
              onClick={() => setShowOnboardingModal(false)}
              className="w-full py-2.5 text-sm font-medium hover:opacity-90 transition-opacity mt-2"
              style={{
                backgroundColor: PALETTE.inchworm,
                color: PALETTE.gunmetal,
              }}
            >
              Let's Get Started
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignLoader;
